import Link from 'next/link';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import SocialShare from '../Social/SocialShare';
import RateRecipeSummary from '../SingleRecipe/RateRecipe/RateRecipeSummary';
import AuthorsByline from '../../widgets/AuthorsByline/AuthorsByline';
import { addTrailingSlash, getArticleDate, isArticleModified } from '../../utils/utility';
import { NS_ARTICLE } from '../../utils/lang';

export default function ArticleHeader(props) {
	const { article } = props;
	const { t, i18n } = useTranslation(NS_ARTICLE);
	const currentLanguage = i18n.language;

	/**
	 * Check atleast one author is valid.
	 * If author is in draft or trashed state in WP, GraphQL returns an object with length 1
	 * This happens cause post meta of the respective post indeed contains author id,
	 * but the author is not published, an edge case.
	 *
	 * Ex: authors: Array(1)
			0: null
			length: 1
	 */
	const authors = (
		article.additionalDetails?.authors?.[0] ? article.additionalDetails?.authors
			: [{ title: 'Flavour Network', link: null }]
	).slice(0, 3);

	const featuredImage = article.featuredImage?.node;

	const featuredTag = article.featuredTag.recipeDetailFeaturedTag;

	const isLongTitleClass = article.title.length > 100 ? 'is-long-title' : '';

	// Check if article is modified
	const articleIsModified = isArticleModified(article.date, article.modified);
	// Get article dates
	const articlePublishedDate = getArticleDate(article.date, currentLanguage);
	const articleModifiedDate = getArticleDate(article.modified, currentLanguage);
	const articleDate = articleIsModified ? `${t('updated')} ${articleModifiedDate}` : articlePublishedDate;

	const authorList = authors.map((author) => ({
		title: author?.title ?? 'Flavour Network',
		link: author?.link ? author.link : null,
	}));

	return (
		<>
			<section className="article-header">
				{featuredTag && featuredTag?.slug !== '' && (
					<div className="article-category">
						<Link
							href={addTrailingSlash(`/tag/${featuredTag?.slug}`)}
							className="article-category-link"
						>
							{featuredTag?.name}
						</Link>
					</div>
				)}
				<h1 className={`article-title ${isLongTitleClass}`}>{article.title}</h1>
				<div className="article-metadata blog-author">
					<div className="article-metadata-divider" />
					{ authorList.length > 0 && <AuthorsByline authors={authorList} prefix={t('by')} /> }
					<div className="article-date">{articleDate}</div>
					<RateRecipeSummary recipe={article} />
					<SocialShare
						contentType="article"
						media={featuredImage?.sourceUrl}
						title={article.title}
					/>
				</div>
			</section>
		</>
	);
}

ArticleHeader.propTypes = {
	article: PropTypes.object.isRequired,
};
